import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { PencilSimple, TextT, ArrowCounterClockwise, Check, BookOpen } from '@phosphor-icons/react';
import FlipBook from './FlipBook';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FONT_OPTIONS = [
  { id: 'serif',  label: 'Classic',  css: '"Playfair Display", Georgia, serif' },
  { id: 'script', label: 'Handwritten', css: '"Dancing Script", "Brush Script MT", cursive' },
  { id: 'sans',   label: 'Modern',   css: '"Inter", system-ui, sans-serif' },
  { id: 'modern', label: 'Bold',     css: '"Montserrat", "Inter", sans-serif' },
];

/**
 * Guest-facing message input for the Memory Book.
 * Two modes: hand-drawn signature (canvas) or typed text (textarea + font picker).
 *
 * Props:
 *   slug       : event slug or id
 *   onSubmitted: optional callback after successful submission
 *   compact    : optional boolean — compact styling when embedded
 */
const MemoryBookMessage = ({ slug, onSubmitted, compact = false, eventType = null }) => {
  const [tab, setTab] = useState('type'); // 'type' | 'draw'
  const [guestName, setGuestName] = useState('');
  const [messageText, setMessageText] = useState('');
  const [fontFamily, setFontFamily] = useState('script');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [flipOpen, setFlipOpen] = useState(false);

  // Hi-DPI canvas setup
  useEffect(() => {
    if (tab !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [tab]);

  const getPt = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = getPt(e);
  }, []);

  const moveDraw = useCallback((e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pt = getPt(e);
    const last = lastPtRef.current || pt;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPtRef.current = pt;
    if (!hasInkRef.current) {
      hasInkRef.current = true;
      setHasInk(true);
    }
  }, []);

  const endDraw = useCallback(() => {
    drawingRef.current = false;
    lastPtRef.current = null;
  }, []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    hasInkRef.current = false;
    setHasInk(false);
  };

  const submit = async () => {
    if (!guestName.trim()) {
      toast.error('Please add your name');
      return;
    }
    if (tab === 'type' && !messageText.trim()) {
      toast.error('Please write a message');
      return;
    }
    if (tab === 'draw' && !hasInk) {
      toast.error('Please draw your signature first');
      return;
    }

    setSubmitting(true);
    let signature_data_url = null;
    if (tab === 'draw') {
      const canvas = canvasRef.current;
      signature_data_url = canvas.toDataURL('image/png');
    }

    try {
      const res = await fetch(`${API}/events/${slug}/guestbook/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_name: guestName.trim(),
          entry_type: tab === 'draw' ? 'drawn' : 'typed',
          message_text: tab === 'type' ? messageText.trim() : null,
          signature_data_url,
          font_family: fontFamily,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      setSubmitted(true);
      toast.success('Added to the Memory Guest Book');
      // Mission tracking: any memory-book entry (typed or drawn) counts.
      try { window.dispatchEvent(new CustomEvent('eventdrop:memorybook_submit')); } catch {}
      if (onSubmitted) onSubmitted();
    } catch (e) {
      toast.error(e.message || 'Could not save — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <div
          className={`rounded-2xl border border-green-500/30 bg-green-500/10 text-center ${compact ? 'p-4' : 'p-6'}`}
          data-testid="memory-book-submitted"
        >
          <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-2">
            <Check size={22} weight="bold" className="text-green-400" />
          </div>
          <h4 className="text-white font-bold text-sm">Your message is in the book</h4>
          <p className="text-gray-400 text-xs mt-1 mb-4">Thanks — the host will see it in their Memory Guest Book PDF.</p>
          <button
            type="button"
            onClick={() => setFlipOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-black text-white"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="memory-book-view-guestbook"
          >
            <BookOpen size={14} weight="fill" /> View guestbook
          </button>
        </div>
        {flipOpen && (
          <FlipBook
            slug={slug}
            onClose={() => setFlipOpen(false)}
            eventNameFallback="Memory Guest Book"
          />
        )}
      </>
    );
  }

  const activeFont = FONT_OPTIONS.find(f => f.id === fontFamily) || FONT_OPTIONS[0];

  return (
    <div
      className={`rounded-2xl border border-white/10 bg-[#1B1C22] ${compact ? 'p-4' : 'p-5'}`}
      data-testid="memory-book-message"
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        >
          <PencilSimple size={16} weight="bold" className="text-white" />
        </div>
        <div className="min-w-0">
          <h4 className="text-white font-bold text-sm truncate">
            {eventType === 'prom' ? '🎓 School Leavers Prom' : 'Leave a Memory Guest Book message'}
          </h4>
          <p className="text-white/50 text-[10px] leading-tight">
            {eventType === 'prom'
              ? 'Leave your final messages for everyone 💌'
              : "Printed in the host's keepsake PDF"}
          </p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          type="button"
          onClick={() => setTab('type')}
          className={`h-9 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
            tab === 'type' ? 'bg-white/10 text-white border border-white/20' : 'bg-transparent text-white/50 border border-white/5 hover:text-white'
          }`}
          data-testid="mb-tab-type"
        >
          <TextT size={14} weight="bold" /> Type
        </button>
        <button
          type="button"
          onClick={() => setTab('draw')}
          className={`h-9 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
            tab === 'draw' ? 'bg-white/10 text-white border border-white/20' : 'bg-transparent text-white/50 border border-white/5 hover:text-white'
          }`}
          data-testid="mb-tab-draw"
        >
          <PencilSimple size={14} weight="bold" /> Sign
        </button>
      </div>

      {/* Name field (shared) */}
      <input
        type="text"
        value={guestName}
        onChange={(e) => setGuestName(e.target.value)}
        maxLength={60}
        placeholder="Your name"
        className="w-full h-10 rounded-lg bg-[#0F1014] border border-white/10 px-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 mb-3"
        data-testid="mb-name-input"
      />

      {tab === 'type' ? (
        <>
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            maxLength={1200}
            placeholder={`Write your message for the host…`}
            rows={5}
            className="w-full rounded-lg bg-[#0F1014] border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 resize-y"
            style={{ fontFamily: activeFont.css }}
            data-testid="mb-text-input"
          />
          <div className="flex items-center gap-1.5 mt-2 overflow-x-auto pb-1">
            {FONT_OPTIONS.map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFontFamily(f.id)}
                className={`flex-shrink-0 px-3 h-8 rounded-lg text-[11px] font-semibold transition-all ${
                  fontFamily === f.id ? 'bg-white/10 text-white border border-white/30' : 'bg-white/0 text-white/50 border border-white/10 hover:text-white'
                }`}
                style={{ fontFamily: f.css }}
                data-testid={`mb-font-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="text-right text-[10px] text-white/30 mt-1">{messageText.length}/1200</div>
        </>
      ) : (
        <>
          <div className="relative rounded-lg border-2 border-dashed border-white/15 bg-white overflow-hidden" style={{ aspectRatio: '2/1' }}>
            <canvas
              ref={canvasRef}
              onMouseDown={startDraw}
              onMouseMove={moveDraw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={moveDraw}
              onTouchEnd={endDraw}
              className="w-full h-full touch-none cursor-crosshair"
              data-testid="mb-signature-canvas"
            />
            {!hasInk && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-gray-400 text-xs">Draw your signature or message here</span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={clearCanvas}
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/60 hover:text-white transition-colors"
            data-testid="mb-clear-signature"
          >
            <ArrowCounterClockwise size={12} weight="bold" /> Clear
          </button>
        </>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={submitting}
        className="w-full h-11 mt-4 rounded-xl text-sm font-bold text-white transition-all active:scale-[0.97] disabled:opacity-50"
        style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        data-testid="mb-submit"
      >
        {submitting ? 'Adding…' : 'Add to Memory Guest Book'}
      </button>

      <button
        type="button"
        onClick={() => setFlipOpen(true)}
        className="w-full h-10 mt-2 rounded-xl text-xs font-bold text-white/80 bg-white/5 hover:bg-white/10 border border-white/10 transition-all flex items-center justify-center gap-2"
        data-testid="mb-view-guestbook"
      >
        <BookOpen size={14} weight="bold" /> View Guestbook
      </button>

      <p className="text-white/30 text-[10px] text-center mt-2">
        Powered by eventdrop.co — goes into the host's keepsake PDF
      </p>

      {flipOpen && (
        <FlipBook slug={slug} onClose={() => setFlipOpen(false)} />
      )}
    </div>
  );
};

export default MemoryBookMessage;
